import { useState, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { SnackbarContext, type Snackbar } from "@/context/SnackbarProvider";
import theme from "@/theme";

export const VITAL_FORM_SNACKBAR_OPTIONS = {
  animate: true,
  direction: "down" as const,
  position: { horizontal: "center" as const, vertical: "bottom" as const },
  duration: 10000,
  borderLeftColor: theme.palette.error.main,
  offset: 100,
};

export interface UseSubmitVitalFormOptions {
  getSubmitPromise: () => Promise<unknown>;
  canSubmit: boolean;
  errorMessage: string;
  /** If returns a Promise, submission stays in loading state until it resolves (e.g. refetch completes). */
  onSuccess?: () => void | Promise<void>;
}

export interface UseSubmitVitalFormResult {
  handleSave: () => void;
  isSubmitting: boolean;
}
export function useSubmitVitalForm({
  getSubmitPromise,
  canSubmit,
  errorMessage,
  onSuccess,
}: UseSubmitVitalFormOptions): UseSubmitVitalFormResult {
  const navigate = useNavigate();
  const { showSnackbar } = useContext(SnackbarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = useCallback(() => {
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    getSubmitPromise()
      .then(() => {
        if (onSuccess) {
          return Promise.resolve(onSuccess());
        }
        navigate(-1);
      })
      .catch(() => {
        showSnackbar({
          message: errorMessage,
          type: "error",
          ...VITAL_FORM_SNACKBAR_OPTIONS,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    canSubmit,
    isSubmitting,
    getSubmitPromise,
    errorMessage,
    onSuccess,
    showSnackbar,
    navigate,
  ]);

  return { handleSave, isSubmitting };
}

export interface RunSubmitWithSnackbarOptions {
  /** Shown only when the update fails. Use e.g. t("Metric.manualEntry.updateError"). */
  errorMessage: string;
  onSuccess?: () => void;
}
export function runSubmitWithSnackbar(
  showSnackbar: (arg: Omit<Snackbar, "visible">) => void,
  promise: Promise<unknown>,
  options: RunSubmitWithSnackbarOptions
): void {
  promise
    .then(() => {
      options.onSuccess?.();
    })
    .catch(() => {
      showSnackbar({
        message: options.errorMessage,
        type: "error",
        ...VITAL_FORM_SNACKBAR_OPTIONS,
      });
    });
}
